import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatCountryCurrency } from "@/lib/utils";

type CountryConfig = {
  countryCode: string;
  currencyCode: string;
  currencySymbol: string;
  currencyName: string;
  /** Format a monetary amount using the active country's locale and symbol. */
  fmt: (amount: number) => string;
};

const FALLBACK: CountryConfig = {
  countryCode: "GH",
  currencyCode: "GHS",
  currencySymbol: "GH₵",
  currencyName: "Ghana Cedi",
  fmt: (n) => formatCountryCurrency(n, "GH", "GH₵"),
};

const CountryContext = createContext<CountryConfig>(FALLBACK);

export const CountryProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<CountryConfig>(FALLBACK);

  useEffect(() => {
    supabase
      .from("platform_countries")
      .select("code, currency_code, currency_symbol, currency_name")
      .eq("is_default", true)
      .eq("is_active", true)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setConfig({
          countryCode: data.code,
          currencyCode: data.currency_code,
          currencySymbol: data.currency_symbol,
          currencyName: data.currency_name,
          fmt: (n) => formatCountryCurrency(n, data.code, data.currency_symbol),
        });
      });
  }, []);

  return <CountryContext.Provider value={config}>{children}</CountryContext.Provider>;
};

/** Returns the active platform country and a convenience `fmt(amount)` formatter. */
export const useCountry = () => useContext(CountryContext);
